import {appLogicalPath, PreparedAppUpload} from './uploadValidation';

export type PreparedAppAsset = {
  contentType: string;
  file: File;
  logicalPath: string;
  sha256: string;
  size: number;
};

// Keep synchronized with dropbox/companyos_app_platform/manifest.py.
const CONTENT_TYPES: Record<string, string> = {
  css: 'text/css; charset=utf-8',
  eot: 'application/vnd.ms-fontobject',
  gif: 'image/gif',
  html: 'text/html; charset=utf-8',
  ico: 'image/x-icon',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  js: 'text/javascript; charset=utf-8',
  json: 'application/json; charset=utf-8',
  map: 'application/json; charset=utf-8',
  mjs: 'text/javascript; charset=utf-8',
  otf: 'font/otf',
  png: 'image/png',
  svg: 'image/svg+xml',
  ttf: 'font/ttf',
  txt: 'text/plain; charset=utf-8',
  wasm: 'application/wasm',
  webp: 'image/webp',
  woff: 'font/woff',
  woff2: 'font/woff2',
  xml: 'application/xml; charset=utf-8',
};

const extensionFor = (path: string) => {
  const extensionIndex = path.lastIndexOf('.');
  return extensionIndex >= 0 ? path.slice(extensionIndex + 1).toLowerCase() : '';
};

export const contentTypeForAppPath = (path: string) =>
  CONTENT_TYPES[extensionFor(path)] ?? 'application/octet-stream';

const abortIfRequested = (signal?: AbortSignal) => {
  if (signal?.aborted) {
    throw signal.reason ?? new DOMException('App publishing was cancelled', 'AbortError');
  }
};

export const sha256File = async (file: File, signal?: AbortSignal) => {
  abortIfRequested(signal);
  const contents = await file.arrayBuffer();
  abortIfRequested(signal);
  const digest = new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', contents));
  abortIfRequested(signal);
  return Array.from(digest, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

export const prepareAppAssets = async (
  upload: PreparedAppUpload,
  signal?: AbortSignal
): Promise<PreparedAppAsset[]> => {
  const files = [...upload.files].sort((left, right) => {
    const leftPath = appLogicalPath(left, upload.name);
    const rightPath = appLogicalPath(right, upload.name);
    return leftPath < rightPath ? -1 : leftPath > rightPath ? 1 : 0;
  });
  const assets: PreparedAppAsset[] = [];

  // Hash sequentially so the browser does not retain every selected file's
  // ArrayBuffer at once while preparing the publication manifest.
  for (const file of files) {
    abortIfRequested(signal);
    const logicalPath = appLogicalPath(file, upload.name);
    assets.push({
      contentType: contentTypeForAppPath(logicalPath),
      file,
      logicalPath,
      sha256: await sha256File(file, signal),
      size: file.size,
    });
  }

  return assets;
};
